import React, { useEffect, useState } from 'react';

// Person avatar: a profile photo when we have one, else a monogram of the
// person's initials on a colour hashed from their identity (email, then name)
// so the same person is the same colour everywhere. Used for renters on the
// operator surfaces and for the signed-in user in their profile/nav.

// Monogram backgrounds. Every colour clears WCAG AA (>= 4.5:1, all verified
// >= 5.0:1) against the white initials.
const PALETTE = [
  '#b91c1c', // red
  '#c2410c', // orange
  '#b45309', // amber
  '#15803d', // green
  '#0f766e', // teal
  '#0369a1', // blue
  '#4338ca', // indigo
  '#7e22ce', // purple
  '#be185d', // pink
  '#475569', // slate
];

// djb2 string hash -> stable palette index. Keyed on whatever identity we have
// so the same person keeps the same colour everywhere.
const colorFor = (key) => {
  const s = String(key || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
};

const initialsFor = (name, email) => {
  const n = (name || '').trim();
  // A name with no letters or digits (e.g. the "—" placeholder some callers
  // pass for a missing renter) is treated as no name at all.
  if (n && /[\p{L}\p{N}]/u.test(n)) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const local = (email || '').split('@')[0];
  if (local) return local.slice(0, 2).toUpperCase();
  return '?';
};

const Avatar = ({
  name,
  email,
  src,
  size = 36,
  className = '',
  style,
  // Set when the person's name is already shown right next to the avatar, so
  // screen readers don't announce it twice.
  decorative = false,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  // A reused instance that failed on a previous photo must retry when the URL
  // changes (e.g. the nav avatar after an account switch).
  useEffect(() => { setImgFailed(false); }, [src]);

  const label = name || email || 'User';
  const a11y = decorative
    ? { 'aria-hidden': true }
    : { role: 'img', 'aria-label': label };

  // `style` is merged last so callers can override any property, including the
  // monogram's own colour/typography.
  const base = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: '50%',
    // Hairline ring keeps the disc edge visible on same-toned backgrounds (dark mode).
    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.14)',
  };

  if (src && !imgFailed) {
    return (
      <img
        {...a11y}
        src={src}
        alt={decorative ? '' : label}
        onError={() => setImgFailed(true)}
        className={className}
        style={{ ...base, objectFit: 'cover', display: 'block', ...style }}
      />
    );
  }

  return (
    <span
      {...a11y}
      className={className}
      style={{
        ...base,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colorFor(email || name),
        color: '#fff',
        fontWeight: 600,
        // Floor keeps two initials legible on the smallest call sites (20–22px).
        fontSize: Math.max(10, Math.round(size * 0.4)),
        lineHeight: 1,
        letterSpacing: '0.02em',
        userSelect: 'none',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {initialsFor(name, email)}
    </span>
  );
};

export default Avatar;
